import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:util:error-utils');

/**
 * Error Utilities
 *
 * Helpers for proper error serialization and logging
 * Prevents "[object Object]" logging issues
 */

/**
 * Serialize an error object for logging/debugging
 * Extracts all useful information from various error types
 */
export function serializeError(error: unknown): Record<string, any> {
  // Handle null/undefined
  if (error == null) {
    return {
      type: 'null_or_undefined',
      value: String(error),
    };
  }

  // Handle Error instances
  if (error instanceof Error) {
    return {
      type: 'Error',
      name: error.name,
      message: error.message,
      stack: error.stack,
      // Include any additional properties
      ...Object.getOwnPropertyNames(error).reduce(
        (acc, key) => {
          if (!['name', 'message', 'stack'].includes(key)) {
            acc[key] = (error as any)[key];
          }
          return acc;
        },
        {} as Record<string, any>
      ),
    };
  }

  // Handle API/Network errors (common in React Query)
  if (typeof error === 'object') {
    const obj = error as any;
    return {
      type: 'object',
      status: obj.status,
      statusText: obj.statusText,
      body: obj.body,
      message: obj.message || String(error),
      // Try to serialize the whole object
      fullObject: JSON.stringify(error, Object.getOwnPropertyNames(error)),
    };
  }

  // Handle primitives
  return {
    type: typeof error,
    value: String(error),
  };
}

/**
 * Format error for user-friendly display
 * Extracts the most relevant error message
 */
export function getErrorMessage(error: unknown): string {
  if (error == null) {
    return 'Unknown error';
  }

  // Error instances
  if (error instanceof Error) {
    return error.message;
  }

  // API errors with body.message
  if (typeof error === 'object') {
    const obj = error as any;
    if (obj.body?.message) return obj.body.message;
    if (obj.message) return obj.message;
    if (obj.statusText) return obj.statusText;
  }

  // Fallback
  return String(error);
}

/**
 * Log error with proper serialization
 * Prevents "[object Object]" in console
 */
export function logError(context: string, error: unknown): void {
  const serialized = serializeError(error);
  logger.error(`❌ [${context}]`, serialized);
}

/**
 * Check if error is a network/connectivity error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('Failed to fetch') ||
      error.name === 'NetworkError'
    );
  }

  if (typeof error === 'object' && error !== null) {
    const obj = error as any;
    return (
      obj.status === 0 || obj.statusText === 'Network request failed' || obj.name === 'NetworkError'
    );
  }

  return false;
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null) {
    const obj = error as any;
    return (
      obj.status === 401 ||
      obj.status === 403 ||
      obj.body?.error === 'invalid_token' ||
      obj.body?.error === 'session_invalid' ||
      obj.body?.error === 'credentials_expired'
    );
  }

  return false;
}
